import React, {useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import moment from 'moment'
import {ClientSummaryCards} from './ClientSummaryCards'
import {VolumeTrendChart} from './VolumeTrendChart'
import {LanguageDistributionChart} from './LanguageDistributionChart'
import {OPIMetricsSection} from './OPIMetricsSection'
import {ScheduledServicesSection} from './ScheduledServicesSection'
import {UpcomingAppointmentsSidebar} from './UpcomingAppointmentsSidebar'
import {
  getClientDashboardOverview,
  getClientFilterOptions,
  type ClientDashboardFilters,
} from './_requests'
import {DashboardFilterBar} from '../components/DashboardFilterBar/DashboardFilterBar'
import {toFilterOptions, lastThirtyDaysRange} from '../components/DashboardFilterBar/utils'
import type {FilterFieldConfig} from '../components/DashboardFilterBar/types'
import {useSelector} from 'react-redux'
import {RootState} from '../../../redux/store'
import {CommonLoading} from '../../../../Utils/commonLoading'
import './ClientPortal.scss'

const DEFAULT_OPI = {
  totalCalls: 0,
  totalMinutes: 0,
  fulfillmentRate: 0,
  avgConnectionTimeSeconds: 0,
  avgDuration: '0m',
}

const DEFAULT_SCHEDULED = {
  totalAppointments: 0,
  totalMinutes: 0,
  fulfillmentRate: 0,
  cancellationRate: 0,
  avgDuration: '0m',
}

const formatPeriodLabel = (filters: ClientDashboardFilters) => {
  const fmt = (d?: string) => (d ? moment(d, 'YYYY-MM-DD').format('MMM DD, YYYY') : '')
  if (filters.startDate && filters.endDate) return `${fmt(filters.startDate)} - ${fmt(filters.endDate)}`
  return 'Selected period'
}

export const ClientPortalDashboard: React.FC = () => {
  const defaultRange = useMemo(lastThirtyDaysRange, [])
  const [filters, setFilters] = useState<ClientDashboardFilters>(defaultRange)
  const [stagedSubAccounts, setStagedSubAccounts] = useState<string[]>([])

  const {items: languages, loading: isFetchingLanguages} = useSelector(
    (state: RootState) => state.languages
  )

  const {data: filterOptions, isLoading: isFetchingOptions} = useQuery(
    'client-dashboard-filter-options',
    getClientFilterOptions,
    {staleTime: 10 * 60 * 1000, refetchOnWindowFocus: false}
  )

  const {data, isLoading, isFetching, isError} = useQuery(
    ['client-dashboard-overview', filters],
    () => getClientDashboardOverview(filters),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      cacheTime: 0,
      retry: 1,
    }
  )

  const subAccountOptions = useMemo(
    () => toFilterOptions(filterOptions?.subAccounts),
    [filterOptions]
  )

  const requesterOptions = useMemo(() => {
    const all = filterOptions?.requesters ?? []
    const scoped = stagedSubAccounts.length
      ? all.filter((r) => r.parentKeys?.some((key) => stagedSubAccounts.includes(key)))
      : all
    return toFilterOptions(scoped)
  }, [filterOptions, stagedSubAccounts])

  const fields: FilterFieldConfig[] = useMemo(
    () => [
      {
        id: 'languages',
        iconPath: '/media/icons/ipivis/language.svg',
        placeholder: 'All Languages',
        options: toFilterOptions(languages),
        isLoading: isFetchingLanguages,
      },
      {
        id: 'requesters',
        iconPath: '/media/icons/duotune/lucid/user.svg',
        placeholder: 'All Requesters',
        options: requesterOptions,
        isLoading: isFetchingOptions,
      },
      {
        id: 'subAccounts',
        iconPath: '/media/icons/ipivis/customer.svg',
        placeholder: 'All Sub Accounts',
        options: subAccountOptions,
        hidden: !isFetchingOptions && subAccountOptions.length === 0,
        isLoading: isFetchingOptions,
      },
    ],
    [languages, isFetchingLanguages, requesterOptions, subAccountOptions, isFetchingOptions]
  )

  const periodLabel = useMemo(() => formatPeriodLabel(filters), [filters])

  const filterBar = (
    <DashboardFilterBar
      fields={fields}
      defaultStartDate={defaultRange.startDate}
      defaultEndDate={defaultRange.endDate}
      isLoading={isFetching}
      onStagedSelectionsChange={(selections) => {
        const next = selections.subAccounts ?? []
        setStagedSubAccounts((prev) =>
          prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next
        )
      }}
      onApply={({startDate, endDate, selections}) =>
        setFilters({
          startDate,
          endDate,
          languages: selections.languages ?? [],
          requesters: selections.requesters ?? [],
          subAccounts: (selections.subAccounts ?? [])
            .map((v) => Number(v))
            .filter((n) => !Number.isNaN(n)),
        })
      }
    />
  )

  if (isLoading && !data) {
    return (
      <div className='client-portal-dashboard'>
        <div className='cp-filter-bar-wrap'>{filterBar}</div>
        <div className='cp-loading'>
          <CommonLoading />
        </div>
      </div>
    )
  }

  return (
    <div className='client-portal-dashboard'>
      <div className='cp-filter-bar-wrap'>{filterBar}</div>

      {isError || !data ? (
        <div className='cp-empty'>Failed to load dashboard data. Please try again.</div>
      ) : (
        <div className='cp-grid'>
          <div className='cp-main'>
            <ClientSummaryCards
              openAppointments={data.openAppointments}
              confirmedAppointments={data.confirmedAppointments}
              interpretationServedMinutes={data.interpretationServedMinutes}
              completionRate={data.completionRate}
              periodLabel={periodLabel}
            />

            <div className='cp-charts-row'>
              <VolumeTrendChart data={data.volumeTrend ?? []} periodLabel={periodLabel} />
              <LanguageDistributionChart
                data={data.languageDistribution ?? []}
                periodLabel={periodLabel}
              />
            </div>

            <OPIMetricsSection
              metrics={data.opiMetrics ?? DEFAULT_OPI}
              statusBuckets={data.opiStatusBuckets ?? []}
              periodLabel={periodLabel}
            />

            <ScheduledServicesSection
              metrics={data.scheduledServicesMetrics ?? DEFAULT_SCHEDULED}
              statusBuckets={data.scheduledStatusBuckets ?? []}
              periodLabel={periodLabel}
            />
          </div>

          <div className='cp-sidebar'>
            <UpcomingAppointmentsSidebar appointments={data.upcomingAppointments ?? []} />
          </div>
        </div>
      )}
    </div>
  )
}
